import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { API_URL } from "./constants";
import "./LeaderboardTab.css";

// Captain-or-above. Shows cumulative wins per voter across socialEvents
// in the chosen date range. Backend aggregates from the wins maps we
// fill in via the RSVPs tab + the GroupMe member roster for nicknames.
//
// "Events" counts events where the user had at least one win recorded
// (a proxy for "played and was tracked") — once we capture full
// attendance lists we can break this into played/attended distinctly.

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const pad2 = (n) => String(n).padStart(2, "0");
const toIso = (year, month0, day) =>
  `${year}-${pad2(month0 + 1)}-${pad2(day)}`;
const daysInMonth = (year, month0) => new Date(year, month0 + 1, 0).getDate();

// All presets resolve to {start, end} in YYYY-MM-DD (America/New_York
// is what the backend uses for "today"; we keep the client honest by
// using the user's local "today" — close enough for a calendar range).
const computePreset = (id, now = new Date()) => {
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();
  switch (id) {
    case "mtd":
      return {
        start: toIso(year, month, 1),
        end: toIso(year, month, day),
      };
    case "lastMonth": {
      const lm = new Date(year, month - 1, 1);
      const lmY = lm.getFullYear();
      const lmM = lm.getMonth();
      return {
        start: toIso(lmY, lmM, 1),
        end: toIso(lmY, lmM, daysInMonth(lmY, lmM)),
      };
    }
    case "ytd":
      return {
        start: toIso(year, 0, 1),
        end: toIso(year, month, day),
      };
    case "last7":
      // 7 days ending today, inclusive
      const from = new Date(now);
      from.setDate(from.getDate() - 6);
      return {
        start: toIso(from.getFullYear(), from.getMonth(), from.getDate()),
        end: toIso(year, month, day),
      };
    default:
      return null;
  }
};

const LeaderboardTab = () => {
  const { getAuthHeaders } = useAuth();
  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Year list: current year and 5 prior. socialEvents probably doesn't
  // have much beyond that; if it ever does, bump this.
  const years = useMemo(
    () => Array.from({ length: 6 }, (_, i) => currentYear - i),
    [currentYear]
  );

  // Default to MTD: this month's first day through today.
  const [preset, setPreset] = useState("mtd");
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  // Resolved range that's actually sent to the backend.
  const [range, setRange] = useState(() => computePreset("mtd", now));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(
    async (rangeStart, rangeEnd) => {
      setLoading(true);
      setError("");
      try {
        const headers = getAuthHeaders();
        const qs = new URLSearchParams({
          type: "leaderboard",
          rangeStart,
          rangeEnd,
        }).toString();
        const resp = await fetch(`${API_URL}?${qs}`, {
          method: "GET",
          headers,
        });
        const body = await resp.json();
        if (!resp.ok) {
          throw new Error(
            body?.error || body?.message || `HTTP ${resp.status}`
          );
        }
        setData(body);
      } catch (err) {
        setError(err.message || "Failed to load leaderboard");
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [getAuthHeaders]
  );

  useEffect(() => {
    load(range.start, range.end);
  }, [range.start, range.end, load]);

  const applyPreset = (id) => {
    const r = computePreset(id, now);
    if (!r) return;
    setPreset(id);
    setRange(r);
  };

  // When the user picks a year/month, clamp the end day to today if
  // the selected month is the current one — otherwise show the whole
  // month. Either way drops out of preset mode.
  const applyMonthYear = (year, month0) => {
    const isCurrent = year === currentYear && month0 === currentMonth;
    const endDay = isCurrent ? now.getDate() : daysInMonth(year, month0);
    setRange({
      start: toIso(year, month0, 1),
      end: toIso(year, month0, endDay),
    });
    setPreset(null);
  };

  const onYearChange = (e) => {
    const y = parseInt(e.target.value, 10);
    setSelectedYear(y);
    applyMonthYear(y, selectedMonth);
  };

  const onMonthChange = (e) => {
    const m = parseInt(e.target.value, 10);
    setSelectedMonth(m);
    applyMonthYear(selectedYear, m);
  };

  return (
    <div className="leaderboard-tab">
      <h2 className="leaderboard-tab__title">Leaderboard</h2>

      <div className="leaderboard-tab__presets">
        {[
          { id: "mtd", label: "Month to date" },
          { id: "lastMonth", label: "Last month" },
          { id: "ytd", label: "Year to date" },
          { id: "last7", label: "Last 7 days" },
        ].map((p) => (
          <button
            key={p.id}
            type="button"
            className={
              "leaderboard-tab__preset" +
              (preset === p.id ? " leaderboard-tab__preset--active" : "")
            }
            onClick={() => applyPreset(p.id)}
            disabled={loading}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="leaderboard-tab__controls">
        <label htmlFor="leaderboard-month-select">Month:</label>
        <select
          id="leaderboard-month-select"
          value={selectedMonth}
          onChange={onMonthChange}
        >
          {MONTHS.map((m, i) => (
            <option key={m} value={i}>
              {m}
            </option>
          ))}
        </select>
        <label htmlFor="leaderboard-year-select">Year:</label>
        <select
          id="leaderboard-year-select"
          value={selectedYear}
          onChange={onYearChange}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="leaderboard-tab__btn"
          onClick={() => load(range.start, range.end)}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {loading && <div className="leaderboard-tab__info">Loading…</div>}
      {error && <div className="leaderboard-tab__error">Error: {error}</div>}

      {data && !loading && (
        <>
          <div className="leaderboard-tab__meta">
            {data.rangeStart} → {data.rangeEnd} · {data.eventsScanned} events
            scanned · {data.eventsWithWins} with wins recorded
          </div>

          {data.leaderboard.length === 0 ? (
            <div className="leaderboard-tab__info">
              No wins recorded in this range yet. Add some on the RSVPs tab.
            </div>
          ) : (
            <table className="leaderboard-tab__table">
              <thead>
                <tr>
                  <th className="leaderboard-tab__rank-col">#</th>
                  <th>Player</th>
                  <th className="leaderboard-tab__num-col">Wins</th>
                  <th className="leaderboard-tab__num-col">Events</th>
                </tr>
              </thead>
              <tbody>
                {data.leaderboard.map((row, idx) => (
                  <tr key={row.userId}>
                    <td className="leaderboard-tab__rank-col">{idx + 1}</td>
                    <td>{row.name}</td>
                    <td className="leaderboard-tab__num-col">
                      {row.totalWins}
                    </td>
                    <td className="leaderboard-tab__num-col">
                      {row.eventsWithWins}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default LeaderboardTab;
